import React, { useMemo } from 'react';
import {
  PromotionFloatingButton,
  ScrollToTopButton,
} from 'shared/src/component';
import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
  type SharedValue,
} from 'react-native-reanimated';
import type { FloatingButton } from 'shared/src/api/promotionsService/getPromotionComponents/types';
import { useHomePromotionComponents } from '../../queries/useHomePromotionComponents';
import { useLogClickHomeFloatingButton } from '../../hooks/useLogClickHomeFloatingButton';
import { useActiveHomeTab } from '../../hooks/useActiveHomeTab';

/**
 * iOS / Android Home 의 floating button 동작과 동일.
 * - floatings[0] (primary): alwaysVisible. 펼침 상태에서 위로 슬라이드.
 * - floatings[1] (secondary): 펼침 상태에서만 fade-in + 더 위로 슬라이드.
 * - ScrollToTopButton: 펼침 상태에서만 fade-in. 항상 바닥 앵커.
 *
 * 펼침 트리거: scrollY > SCROLL_EXPAND_THRESHOLD
 */
const SCROLL_EXPAND_THRESHOLD = 10;
const PRIMARY_TRANSLATE_Y = -52; // Android TOP_BUTTON_LAYOUT_TOP_DP
const SECONDARY_TRANSLATE_Y = -112; // Android secondary translationY
const ANIMATION_DURATION = 150; // iOS UIView.animate 0.15s

type Props = {
  scrollY: SharedValue<number>;
  onScrollToTopPress: () => void;
  /** safeArea 등으로 결정되는 바닥 패딩. 기본 16. */
  bottomOffset?: number;
};

export const HomeFloatingButtons = (props: Props) => {
  return (
    <React.Suspense fallback={null}>
      <HomeFloatingButtonsContent {...props} />
    </React.Suspense>
  );
};

const HomeFloatingButtonsContent = ({
  scrollY,
  onScrollToTopPress,
  bottomOffset = 16,
}: Props) => {
  const activeTab = useActiveHomeTab();
  const { data } = useHomePromotionComponents();

  const { primary, secondary, promotionSno } = useMemo(() => {
    const buttons = data?.component?.floating_buttons ?? [];
    return {
      primary: buttons[0] ?? null,
      secondary: buttons[1] ?? null,
      promotionSno: data?.component?.promotion_sno,
    };
  }, [data]);

  const isExpanded = useDerivedValue(
    () => scrollY.value > SCROLL_EXPAND_THRESHOLD,
  );

  const primaryStyle = useAnimatedStyle(() => ({
    transform: [
      {
        translateY: withTiming(isExpanded.value ? PRIMARY_TRANSLATE_Y : 0, {
          duration: ANIMATION_DURATION,
        }),
      },
    ],
  }));

  const scrollToTopStyle = useAnimatedStyle(() => ({
    opacity: withTiming(isExpanded.value ? 1 : 0, {
      duration: ANIMATION_DURATION,
    }),
  }));

  const secondaryStyle = useAnimatedStyle(() => ({
    opacity: withTiming(isExpanded.value ? 1 : 0, {
      duration: ANIMATION_DURATION,
    }),
    transform: [
      {
        translateY: withTiming(isExpanded.value ? SECONDARY_TRANSLATE_Y : 0, {
          duration: ANIMATION_DURATION,
        }),
      },
    ],
  }));

  // 웹뷰 탭에서는 floating 영역 자체를 숨김 (iOS HomePagerVC 와 동일)
  if (activeTab?.type === 'WEBVIEW') return null;

  return (
    <>
      {/* Primary — 항상 노출. 펼침 시 위로 슬라이드 */}
      {primary && (
        <Animated.View
          style={[
            { position: 'absolute', right: 16, bottom: bottomOffset },
            primaryStyle,
          ]}
          pointerEvents={'box-none'}
        >
          <PromotionButtonWithLog
            button={primary}
            buttonIdx={0}
            promotionSno={promotionSno}
          />
        </Animated.View>
      )}

      {/* ScrollToTop — 펼침 시 fade-in. 바닥 앵커 */}
      <Animated.View
        style={[
          { position: 'absolute', right: 16, bottom: bottomOffset },
          scrollToTopStyle,
        ]}
        pointerEvents={'box-none'}
      >
        <ScrollToTopButton
          testID={'home-scroll-to-top'}
          onPress={onScrollToTopPress}
        />
      </Animated.View>

      {/* Secondary — 펼침 시 fade-in + 더 위로 슬라이드 */}
      {secondary && (
        <Animated.View
          style={[
            { position: 'absolute', right: 16, bottom: bottomOffset },
            secondaryStyle,
          ]}
          pointerEvents={'box-none'}
        >
          <PromotionButtonWithLog
            button={secondary}
            buttonIdx={1}
            promotionSno={promotionSno}
          />
        </Animated.View>
      )}
    </>
  );
};

const PromotionButtonWithLog = ({
  button,
  buttonIdx,
  promotionSno,
}: {
  button: FloatingButton;
  buttonIdx: number;
  promotionSno?: number;
}) => {
  const onClickLog = useLogClickHomeFloatingButton(button, buttonIdx);
  return (
    <PromotionFloatingButton
      button={button}
      promotionSno={promotionSno}
      onClickLog={onClickLog}
    />
  );
};
